import { renderInvoiceStatus, renderInvoiceStatusReason, renderInvoiceSummaryStatus } from './decorators/decorator';
import { IntlShape } from 'react-intl/src/types';
import { PortalUser } from '../../components';
import { ColDef } from 'ag-grid-community';
import { convertFromSpecifiedDateFormatToUserDefined } from '../users/UserFormatPreferenceServices';
import { GridFilter } from './interfaces';

export const invoiceListDefaultColDef = {
    editable: true,
    /* enableRowGroup: true,
    enablePivot: true,
    enableValue: true,*/
    sortable: true,
    resizable: true,
};

const textFilterParam =  {
    suppressAndOrCondition: true,
    filterOptions: ['equals', 'contains', 'startsWith', 'endsWith', 'blank', 'notBlank']
};

const numberFilterParam = {
    suppressAndOrCondition: true,
    filterOptions: ['equals', 'lessThan','greaterThan','inRange','blank', 'notBlank']
};

const dateFilterParam = {
    suppressAndOrCondition: true,
    filterOptions: ['equals', 'lessThan','greaterThan','inRange','blank', 'notBlank']
};

export const invoiceListColumns = (intl: IntlShape, user: PortalUser): ColDef[] => [
    {
        field: 'message_id',
        colId: 'message_id',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-message_id' }),
        width: 250,
        lockPosition: true,
        lockVisible: true,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'partnerno',
        colId: 'partnerno',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-partnerno' }),
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'sys_id',
        colId: 'sys_id',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-sys_id' }),
        width: 150,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },
    {
        field: 'company',
        colId: 'company',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-company' }),
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },

    {
        field: 'reference',
        colId: 'reference',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-reference' }),
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },

    {
        field: 'duplicate',
        colId: 'duplicate',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-duplicate' }),
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },

    {
        field: 'cross_border',
        colId: 'cross_border',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-cross_border' }),
        width: 70,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: ['Y', 'N'],
            debounceMs: 2000,
        },
        hide: true,
    },
    {
        field: 'process',
        colId: 'process',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_m_invprocess' }),
        width: 120,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
        },
    },
    {
        field: 'm_interface',
        colId: 'm_interface',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-m_interface' }),
        width: 200,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },

    {
        field: 'step_number',
        colId: 'step_number',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-step_number' }),
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },
    {
        field: 'step',
        colId: 'step',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-step' }),
        width: 100,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },
    {
        field: 'status',
        colId: 'status',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-status' }),
        width: 80,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
        cellRenderer: (props) => renderInvoiceStatus(props.value),
    },

    {
        field: 'status_reason',
        colId: 'status_reason',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-status_reason' }),
        width: 200,
        cellRenderer: (props) => renderInvoiceStatusReason(props.value),
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },

    {
        field: 'creation_ts',
        colId: 'creation_ts',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-creation_ts' }),
        width: 130,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParam,
        valueGetter: (params) => {
            if (params.data.creation_ts && params.data.creation_ts.length > 10) {
                return (
                    convertFromSpecifiedDateFormatToUserDefined(
                        user,
                        params.data.creation_ts.substring(0, 10),
                        'YYYY-MM-DD'
                    ) + params.data.creation_ts.substring(10)
                );
            }
            return '';
        },
    },
    {
        field: 'sdi_number',
        colId: 'sdi_number',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-sdi_number' }),
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'invoice_date',
        colId: 'invoice_date',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-invoice_date' }),
        width: 150,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParam,
        valueGetter: (params) => {
            if (params.data.invoice_date) {
                return convertFromSpecifiedDateFormatToUserDefined(user, params.data.invoice_date, 'YYYY-MM-DD');
            }
            return '';
        },
    },
    {
        field: 'log_status_id',
        colId: 'log_status_id',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-log_status_id' }),
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
        hide: true,
    },
    {
        field: 'log_obj_id',
        colId: 'log_obj_id',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-log_obj_id' }),
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
        hide: true,
    },

    {
        field: 'edi_agreement_date',
        colId: 'edi_agreement_date',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-edi_agreement_date' }),
        width: 150,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParam,
        hide: true,
    },
    {
        field: 'ebox_folder',
        colId: 'ebox_folder',
        headerName: intl.formatMessage({ id: 'eim-doc-monitor-grid-ebox_folder' }),
        width: 150,
        filter: false,
        hide: true,
    },
    {
        field: 'm_format',
        colId: 'm_format',
        headerName: intl.formatMessage({ id: 'ag-grid-format' }),
        width: 150,
        hide: false,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
    },
    {
        field: 'vin_number',
        colId: 'vin_number',
        headerName: intl.formatMessage({ id: 'document-overview-grid-header-vin-number' }),
        width: 150,
        hide: false,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'process_code',
        colId: 'process_code',
        headerName: intl.formatMessage({ id: 'survey-customer-channel-type-grid-header-processCode' }),
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
        hide: false,
    },
    {
        field: 'route_id',
        colId: 'route_id',
        headerName: 'Route Id',
        width: 150,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
        hide: false,
    },
    {
        field: 'group_name',
        colId: 'group_name',
        headerName: intl.formatMessage({ id: 'ag-grid-group' }),
        width: 150,
        filter: 'agSetColumnFilter',
        filterParams: {
            values: [],
            debounceMs: 2000,
            refreshValuesOnOpen: true,
        },
        hide: false,
    },
    {
        field: 'log_info_id',
        colId: 'log_info_id',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_log_info_id' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'ext_reference',
        colId: 'ext_reference',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_ext_reference' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'channel',
        colId: 'channel',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_channel' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'target_directory',
        colId: 'target_directory',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_target_directory' }),
        width: 200,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'insert_user',
        colId: 'insert_user',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_insert_user' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'update_user',
        colId: 'update_user',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_update_user' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'insert_ts',
        colId: 'insert_ts',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_insert_ts' }),
        width: 180,
        hide: true,
        filter: 'agDateColumnFilter',
        valueGetter: (params) => {
            if (params.data.insert_ts && params.data.insert_ts.length > 10) {
                return (
                    convertFromSpecifiedDateFormatToUserDefined(
                        user,
                        params.data.insert_ts.substring(0, 10),
                        'YYYY-MM-DD'
                    ) + params.data.insert_ts.substring(10)
                );
            }
            return '';
        },
    },
    {
        field: 'original_log_obj_id',
        colId: 'original_log_obj_id',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_original_log_obj_id' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'system_id',
        colId: 'system_id',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_system_id' }),
        width: 150,
        hide: true,
    },
    {
        field: 'partner_country',
        colId: 'partner_country',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_partner_country' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'partner_name',
        colId: 'partner_name',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_partner_name' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'document_available',
        colId: 'document_available',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_document_available' }),
        width: 150,
        hide: true,
    },
    {
        field: 'file_name',
        colId: 'file_name',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_file_name' }),
        width: 250,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'id_partner',
        colId: 'id_partner',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_id_partner' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'clearing_house',
        colId: 'clearing_house',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_clearing_house' }),
        width: 150,
        hide: true,
    },
    {
        field: 'shippartnerno',
        colId: 'shippartnerno',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_shipPartnerNo' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'edp_relevant',
        colId: 'edp_relevant',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_edp_relevant' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'bmw_int_reference',
        colId: 'bmw_int_reference',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_bmw_int_reference' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'apar',
        colId: 'apar',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_apar' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'document_type',
        colId: 'document_type',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_document_type' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'gross_amount',
        colId: 'gross_amount',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_gross_amount' }),
        width: 150,
        hide: true,
        filter: 'agNumberColumnFilter',
        filterParams: numberFilterParam,
    },
    {
        field: 'tax_amount',
        colId: 'tax_amount',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_tax_amount' }),
        width: 150,
        hide: true,
        filter: 'agNumberColumnFilter',
        filterParams: numberFilterParam,
    },
    {
        field: 'currency',
        colId: 'currency',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_currency' }),
        width: 100,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'final_completion',
        colId: 'final_completion',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_final_completion' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'm_provider',
        colId: 'm_provider',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_m_provider' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'received_date',
        colId: 'received_date',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_received_date' }),
        width: 150,
        hide: true,
        filter: 'agDateColumnFilter',
        filterParams: textFilterParam,
        valueGetter: (params) => {
            if (params.data.received_date && params.data.received_date.length > 10) {
                return convertFromSpecifiedDateFormatToUserDefined(
                    user,
                    params.data.received_date.substring(0, 10),
                    'YYYY-MM-DD'
                );
            }
            return '';
        },
    },
    {
        field: 'complete_date',
        colId: 'complete_date',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_complete_date' }),
        width: 150,
        hide: true,
        filter: 'agDateColumnFilter',
        filterParams: textFilterParam,
        valueGetter: (params) => {
            if (params.data.complete_date && params.data.complete_date.length > 10) {
                return convertFromSpecifiedDateFormatToUserDefined(
                    user,
                    params.data.complete_date.substring(0, 10),
                    'YYYY-MM-DD'
                );
            }
            return '';
        },
    },
    {
        field: 'm_invprocess',
        colId: 'm_invprocess',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_m_invprocess' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'md_process_id',
        colId: 'md_process_id',
        headerName: intl.formatMessage({ id: 'eim_doc_overview_md_process_id' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    },
    {
        field: 'incident_no',
        colId: 'incident_no',
        headerName: intl.formatMessage({ id: 'incident-number' }),
        width: 150,
        hide: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParam,
    }
];

export const summaryInvoiceListColumns = [
    {
        field: 'partnerno',
        headerName: 'PARTNERNO',
        width: 120,
        filter: false,
        cellRenderer: 'agGroupCellRenderer',
    },
    {
        field: 'company',
        headerName: 'COD',
        width: 80,
        filter: false,
        filterParams: {
            values: [],
            debounceMs: 2000,
        },
    },
    {
        field: 'reference',
        headerName: 'REFERENCE',
        width: 150,
        filter: false,
    },
    {
        field: 'm_process',
        headerName: 'PROCESS',
        width: 120,
        filter: false,
        filterParams: {
            values: [],
            debounceMs: 2000,
        },
    },
    {
        field: 'inv_status',
        headerName: 'STATUS',
        width: 80,
        filter: false,
        filterParams: {
            values: [],
            debounceMs: 2000,
        },
        cellRenderer: (props) => renderInvoiceSummaryStatus(props.value),
    },
    {
        field: 'insert_ts',
        headerName: 'RECEIVED_DATE',
        width: 130,
        filter: false,
    },
    {
        field: 'complete_date',
        headerName: 'COMPLETE DATE',
        width: 130,
        filter: false,
    },
];

export const removeFilterDuplicates = (originalArray:GridFilter[]):GridFilter[]=> {
    return originalArray.reduce((accumulator, current) => {
        const theValue = current?.value?.trim().toUpperCase();
        if (theValue && !accumulator.find(item => item === theValue)) {
            accumulator.push(theValue);
        }
        return accumulator;
    }, []).map(item => ({ text: item, value: item }) );
}
